import { Theme, useTheme } from "@amx/ux-twoui-theming";
import { IIconSubset, initializeIcons, registerIcons, unregisterIcons } from "@fluentui/react";
import React, { CSSProperties, useEffect } from "react";
import { useComponentLogger } from "../../shared/logging";
import { BaseProps, validateProps } from "../../shared/props";
import { ComponentValidator, validateComponent } from "../../shared/validation";
import { getColorFromCorrectPalette } from "../../styles/colors";
import { IconBackplateSize, IconColor, IconName, IconSize } from "../../styles/icons";
import { iconNameSet } from "./Icon.types";

/**
 * Icon properties
 */
export interface IconProps extends BaseProps {
    /**
     * Name of the icon to render (required)
     */
    readonly iconName: IconName;

    /**
     * Size of the icon
     */
    readonly size?: IconSize | IconBackplateSize;

    /**
     * Color of the icon - must be in our palette
     * If no color is specified, the color of the icon will be black
     */
    readonly color?: IconColor;

    /**
     * Whether or not icon should display as disabled
     */
    readonly disabled?: boolean;
}

/**
 * Icon Constants
 */
export const amcIconsFontFamily = "AMC Icons";

/**
 * Icon component
 */
export const Icon: React.FC<IconProps> = ({
    iconName: consumerIconName,
    size = "16px",
    color,
    disabled = false,
    "aria-label": ariaLabel,
    ...otherProps
}) => {
    useComponentLogger("Icon", otherProps.id);

    validateProps("Icon", otherProps);

    const theme = useTheme();
    const iconName = iconNameSet.has(consumerIconName) ? consumerIconName : "BrokenFile";
    const styles = getStyles(theme, size, color, disabled);

    useEffect(() => {
        // As some icons come from CMS at runtime, we want insight into
        // whether we have faulty icon names in Compass, for example
        const iconNameValidator: ComponentValidator = () => {
            if (!iconNameSet.has(consumerIconName)) {
                return `Invalid icon name: ${consumerIconName}. Please refer to the TwoUI Icon component for a list of all valid icon names.`;
            }

            return undefined;
        };

        validateComponent("Icon", [iconNameValidator]);
    }, [consumerIconName]);

    return (
        <span
            role={ariaLabel ? "img" : "presentation"}
            aria-hidden={ariaLabel ? false : true}
            data-icon-name={iconName}
            className={iconName}
            style={styles}
            aria-label={ariaLabel}
            {...otherProps}
        />
    );
};

const getStyles = (theme: Theme, fontSize: IconSize | IconBackplateSize, color?: IconColor, disabled?: boolean): CSSProperties => {
    const computedColor = disabled ? "neutralTertiaryAlt" : color;

    return {
        fontFamily: amcIconsFontFamily,
        ...(computedColor && { color: getColorFromCorrectPalette(theme, computedColor) }),
        fontSize
    };
};

export const initializeIconsTwoUi = () => {
    initializeIcons();
};

/**
 * Registers the given TowUI icons.
 * NOTE: Any icons registered using this function will only work in the current React app.
 *       If another React app on the page tries to use the icon, React will throw an error.
 *       For example if app-feedback registers the "Contact" icon and app-home tries to use it.
 *       This will be the case until https://github.com/microsoft/fluentui/issues/17326 is fixed.
 * @param icons List of icons to register. Non TwoUI icons will be ignored.
 */
export const registerIconsTwoUi = (icons: readonly IconName[]) => {
    const iconsToUnregister: string[] = [];
    const iconsToRegister: IIconSubset["icons"] = {};

    icons.forEach(icon => {
        if (iconNameSet.has(icon)) {
            iconsToUnregister.push(icon);
            iconsToRegister[icon] = <Icon iconName={icon} />;
        }
    });

    unregisterIcons(iconsToUnregister);
    registerIcons({ icons: iconsToRegister });
};
